import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes, FaPhone } from "react-icons/fa";
import {
  HELPDESK_PHONE_DISPLAY,
  EMERGENCY_PHONE_DISPLAY,
  HELPDESK_PHONE_TEL,
  EMERGENCY_PHONE_TEL,
} from "../utils/clinicContact";
import "./Header.css";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/services", label: "Services" },
    { path: "/patient-info", label: "Patient Info" },
    { path: "/testimonials", label: "Testimonials" },
    { path: "/contact", label: "Contact" },
  ];

  return (
    <header className={`header ${isScrolled ? "header-scrolled" : ""}`}>
      <div className="container">
        <div className="header-content">
          {/* Logo */}
          <Link to="/" className="logo">
            <div className="logo-container">
              <img
                src={require("../assets/logo-withoutBG.png")}
                alt="SATYA HEART CLINIC Logo"
                className="logo-main"
              />
              <img
                src={require("../assets/satya-beside-logo.jpg")}
                alt="SATYA HEART CLINIC"
                className="logo-secondary"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="nav-desktop">
            <ul className="nav-list">
              {navItems.map((item) => (
                <li key={item.path} className="nav-item">
                  <Link
                    to={item.path}
                    className={`nav-link ${
                      location.pathname === item.path ? "active" : ""
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact Info */}
          <div className="header-contact">
            <a href={`tel:${HELPDESK_PHONE_TEL}`} className="contact-phone">
              <FaPhone />
              <span>{HELPDESK_PHONE_DISPLAY}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            onClick={toggleMenu}
            aria-label="Toggle mobile menu"
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container">
              <nav className="nav-mobile">
                <ul className="nav-list-mobile">
                  {navItems.map((item) => (
                    <li key={item.path} className="nav-item-mobile">
                      <Link
                        to={item.path}
                        className={`nav-link-mobile ${
                          location.pathname === item.path ? "active" : ""
                        }`}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>

                {/* Mobile Contact Info */}
                <div className="mobile-contact">
                  <a
                    href={`tel:${HELPDESK_PHONE_TEL}`}
                    className="mobile-contact-phone"
                  >
                    <FaPhone />
                    <span>Helpdesk: {HELPDESK_PHONE_DISPLAY}</span>
                  </a>
                  <a
                    href={`tel:${EMERGENCY_PHONE_TEL}`}
                    className="mobile-contact-phone mobile-contact-phone--emergency"
                  >
                    <span>Emergency: {EMERGENCY_PHONE_DISPLAY}</span>
                  </a>
                  <p className="mobile-hours">
                    Monday to Saturday, 6:00 PM - 9:00 PM
                  </p>
                  <p className="mobile-hours-note">
                    A prior appointment is preferred.
                  </p>
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
